import React, {useMemo, useRef, useState} from 'react'
import { Box, Stack, Slider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

type TimelinePoint = {
  date: string
}

type PlayerProps = {
  max: number
  onChange: (index: number) => void
  children?: React.ReactNode
  period?: string
  timeline?: TimelinePoint[]
}

const DAY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: '2-digit',
  month: 'short'
}

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit'
}

const getPeriodDuration = (period?: string) => {
  const daysByPeriod = {
    '-1d': 1,
    '-3d': 3,
    '-7d': 7
  }

  return period && period in daysByPeriod
    ? moment.duration(daysByPeriod[period as keyof typeof daysByPeriod], 'days').asMilliseconds()
    : null
}

const normalizeTimestamp = (timestamp: string | number | Date) => {
  if (timestamp instanceof Date) {
    return timestamp
  }

  const numericTimestamp = typeof timestamp === 'number'
    ? timestamp
    : Number(timestamp)

  if (!Number.isNaN(numericTimestamp)) {
    return new Date(numericTimestamp < 10000000000 ? numericTimestamp * 1000 : numericTimestamp)
  }

  return new Date(timestamp)
}

const formatDate = (
  date: string | Date,
  dayFormatter: Intl.DateTimeFormat,
  timeFormatter: Intl.DateTimeFormat
) => {
  const parsedDate = normalizeTimestamp(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return String(date)
  }

  return `${dayFormatter.format(parsedDate)} - ${timeFormatter.format(parsedDate)}`
}

const formatDateByPeriod = (
  date: string | Date,
  dayFormatter: Intl.DateTimeFormat,
  timeFormatter: Intl.DateTimeFormat
) => {
  const parsedDate = normalizeTimestamp(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return String(date)
  }

  return `${dayFormatter.format(parsedDate)} - ${timeFormatter.format(parsedDate)}`
}

const getIntervalEnd = (timeline: TimelinePoint[]) => {
  const lastDate = timeline[timeline.length - 1]?.date
  if (!lastDate) {
    return new Date()
  }

  const parsedDate = normalizeTimestamp(lastDate)
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate
}

const getIntervalDate = (
  index: number,
  sliderMax: number,
  period: string | undefined,
  timeline: TimelinePoint[]
) => {
  if (timeline.length === 0) {
    return null
  }

  const duration = getPeriodDuration(period)
  if (!duration) {
    const selectedDate = timeline[index]?.date
    return selectedDate ? normalizeTimestamp(selectedDate) : null
  }

  const end = getIntervalEnd(timeline)
  const start = new Date(end.getTime() - duration)
  const ratio = sliderMax > 0 ? index / sliderMax : 0

  return new Date(start.getTime() + duration * ratio)
}

const Player = ({max, onChange, children, period, timeline = []}: PlayerProps) => {
  const { i18n } = useTranslation()
  const [animated, setAnimated] = useState<boolean>(false)
  const [playButtonHovered, setPlayButtonHovered] = useState<boolean>(false)
  const [index, setIndex] = useState<number>(0)
  const intervalRef = useRef<number | null>(null)
  const indexRef = useRef<number>(0)
  const sliderMax = Math.max(max - 1, 0)
  const dayFormatter = useMemo(() => new Intl.DateTimeFormat(i18n.language, DAY_FORMAT_OPTIONS), [i18n.language])
  const timeFormatter = useMemo(() => new Intl.DateTimeFormat(i18n.language, TIME_FORMAT_OPTIONS), [i18n.language])

  const selectedDate = getIntervalDate(index, sliderMax, period, timeline)

  const handlePlayPause = () => {
    if (sliderMax === 0) {
      return
    }

    if (intervalRef.current) {
      setAnimated(false)
      clearInterval(intervalRef.current)
      intervalRef.current = null
    } else {
      setAnimated(true)
      intervalRef.current = window.setInterval(() => {
        if (indexRef.current >= sliderMax) {
          setAnimated(false)
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          indexRef.current = 0
          setIndex(0)
          onChange(0)
        } else {
          const newIndex = Math.min(indexRef.current + 6, sliderMax)
          indexRef.current = newIndex
          setIndex(newIndex)
          onChange(newIndex)
        }
      }, 100)
    }
  }

  return (
    <Stack
      direction={'row'}
      alignItems="center"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        transformOrigin: 'center',
        minHeight: 38,
        width: 'min(calc(100% - 112px), 560px)',
        zIndex: 100,
        backgroundColor: 'white',
        borderRadius: '999px',
        px: 1.5,
        py: 0.75,
        boxShadow: '0 8px 22px rgba(30, 41, 59, 0.14), 0 1px 3px rgba(30, 41, 59, 0.08)',
        gap: 1.5
      }}
    >
      <Box
        component="button"
        type="button"
        disabled={sliderMax === 0}
        onClick={handlePlayPause}
        onMouseEnter={() => setPlayButtonHovered(true)}
        onMouseLeave={() => setPlayButtonHovered(false)}
        onKeyDown={(e) => e.key === 'Enter' && handlePlayPause()}
        aria-label={animated ? 'Pause' : 'Lecture'}
        sx={{
          width: 24,
          height: 24,
          flex: '0 0 24px',
          border: 0,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          color: '#344054',
          cursor: sliderMax === 0 ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          '&:hover': {
            color: '#0879A6'
          },
          '& i': {
            fontSize: 12
          }
        }}
      >
        <i className={`fa-${animated || playButtonHovered ? 'solid' : 'light'} fa-${animated ? 'pause' : 'play'}`} />
      </Box>
      {selectedDate && (
        <Typography
          variant="caption"
          title={formatDate(selectedDate, dayFormatter, timeFormatter)}
          sx={{
            display: { xs: 'none', md: 'block' },
            color: '#667085',
            flex: '0 0 128px',
            fontSize: 11,
            lineHeight: 1,
            overflow: 'hidden',
            textTransform: 'capitalize',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {formatDateByPeriod(selectedDate, dayFormatter, timeFormatter)}
        </Typography>
      )}
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Slider
          value={index}
          min={0}
          max={sliderMax}
          step={1}
          size={'small'}
          valueLabelDisplay={selectedDate ? 'auto' : 'off'}
          valueLabelFormat={(value) => {
            const date = getIntervalDate(value as number, sliderMax, period, timeline)
            return date ? formatDateByPeriod(date, dayFormatter, timeFormatter) : ''
          }}
          disabled={sliderMax === 0}
          onChange={(_, value) => {
            setAnimated(false)
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
            indexRef.current = value as number
            setIndex(value as number)
            onChange(value as number)
          }}
          sx={{
            height: 4,
            py: 0,
            '& .MuiSlider-rail': {
              opacity: 1,
              backgroundColor: '#E8EDF2'
            },
            '& .MuiSlider-track': {
              border: 'none',
              backgroundColor: '#3B82F6'
            },
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              backgroundColor: '#3B82F6',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.14)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.16)'
              },
              '&:before': {
                boxShadow: 'none'
              }
            },
            '& .MuiSlider-valueLabel': {
              fontSize: 11,
              backgroundColor: '#344054',
              borderRadius: '6px',
              textTransform: 'capitalize'
            }
          }}
        />
      </Stack>
      {
        children
      }
    </Stack>
  )
}

export default Player